import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  QueryKey,
  useQueryClient,
} from "@tanstack/react-query";
import { useApiHelperAppContext } from "../appContexts/ApiHelperAppContext";
import axiosInstance, { setAxiosToken } from "../helpers/axiosHelper";
import { getErrorToast, getSuccessToast } from "../utilities/toastUtility";
import axios from "axios";
import { IOperationResultViewModel } from "../../models/operationResultViewModel";
import { useAuthentication } from "./useAuthentication";
import { useNavigate } from "react-router-dom";
import { routePaths } from "../routes/routePaths";

interface UseApiMutationRequest {
  endpoint: string;
  method?: "post" | "put" | "delete";
  invalidateKeys?: QueryKey[];
  globalIsLoading?: boolean;
  additionalParams?: string;
  queryParams?: Record<string, string | number | boolean | null | undefined>;
  disableSuccessToast?: boolean;
  successMessage?: string;
  disableErrorToast?: boolean;
  errorMessage?: string;
  payload?: any;
  disable401Redirect?: boolean;
}

export function useApiMutation<TData = unknown>(
  options?: UseMutationOptions<TData, Error, UseApiMutationRequest>
): UseMutationResult<TData, Error, UseApiMutationRequest> {
  const queryClient = useQueryClient();
  const { setIsLoading: contextIsLoading } = useApiHelperAppContext();
  const { currentUser } = useAuthentication();
  const navigate = useNavigate();

  return useMutation<TData, Error, UseApiMutationRequest>({
    mutationFn: async (requestData) => {
      // Ensure token is set before making the request
      setAxiosToken(currentUser?.authToken);

      const {
        method = "post",
        globalIsLoading,
        additionalParams,
        queryParams,
        endpoint,
        invalidateKeys,
        disableSuccessToast,
        successMessage,
        disableErrorToast,
        errorMessage,
        payload: explicitPayload,
        ...restPayload
      } = requestData;

      // Use explicit payload if provided, otherwise use rest of properties
      const payload = explicitPayload !== undefined ? explicitPayload : restPayload;

      if (globalIsLoading) {
        contextIsLoading(true);
      }

      if (additionalParams) {
        requestData.endpoint = `${requestData.endpoint}?${additionalParams}`;
      }

      if (queryParams) {
        const params = new URLSearchParams(
          Object.entries(queryParams)
            .filter(([_, val]) => val !== undefined && val !== null)
            .map(([key, val]) => [key, String(val)])
        ).toString();

        requestData.endpoint = `${requestData.endpoint}?${params}`;
      }

      try {
        const res = await axiosInstance[method]<TData>(
          requestData.endpoint,
          payload
        );
        return res.data;
      } finally {
        if (globalIsLoading) {
          contextIsLoading(false);
        }
      }
    },
    ...options,
    // Custom onSuccess logic - e.g. invalidate queries
    onSuccess: (data, variables, context) => {
      const dataAsOperationResult = data as IOperationResultViewModel<unknown>;

      if (!variables.disableSuccessToast) {
        getSuccessToast(
          variables.successMessage ??
            dataAsOperationResult?.resultMessage ??
            data?.toString()
        );
      }

      if (variables.invalidateKeys) {
        variables.invalidateKeys.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      }
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context);

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;

        if ((status === 401 || status === 403) && !variables.disable401Redirect) {
          navigate(routePaths.logout);
          return;
        }

        const responseData = error.response?.data;

        // Extract error message, handling various API response formats
        // Ensures only strings are passed to the toast (prevents React error #31)
        let errorMessage: string;

        if (typeof responseData === "string") {
          // Plain string response
          errorMessage = responseData;
        } else if (responseData?.message && typeof responseData.message === "string") {
          // Custom API format with message property
          errorMessage = responseData.message;
        } else if (responseData?.title && typeof responseData.title === "string") {
          // ASP.NET Core validation error format
          errorMessage = responseData.title;
        } else if (responseData?.errors && typeof responseData.errors === "object") {
          // Extract first validation error from ASP.NET Core errors object
          const errorValues = Object.values(responseData.errors).flat();
          const firstError = errorValues[0];
          errorMessage = typeof firstError === "string"
            ? firstError
            : "Възникна грешка при валидацията.";
        } else {
          errorMessage = variables.errorMessage || "Възникна неочаквана грешка.";
        }

        if (!variables.disableErrorToast) {
          getErrorToast(errorMessage);
        }
      } else {
        const fallbackMessage =
          variables.errorMessage ||
          error.message ||
          "Възникна неочаквана грешка.";
        if (!variables.disableErrorToast) {
          getErrorToast(fallbackMessage);
        }
      }
    },
  });
}
